import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Recording, RecordingEvent } from '../../types';

interface PlayerState {
  // Recording data
  recording: Recording | null;
  events: RecordingEvent[];
  duration: number;
  
  // Playback state
  isPlaying: boolean;
  currentTime: number;
  playbackSpeed: number;
  
  // Audio state
  audioUrl: string | null;
  audioElement: HTMLAudioElement | null;
  isAudioReady: boolean;
  hasAudioError: boolean;
  
  // Canvas state
  canvasWidth: number;
  canvasHeight: number;
  scale: number;
  
  // UI state
  isFullscreen: boolean;
  isSeeking: boolean;
  showControls: boolean;
  
  // Actions
  setRecording: (recording: Recording) => void;
  setAudioUrl: (url: string) => void;
  setAudioElement: (element: HTMLAudioElement) => void;
  
  // Playback controls
  play: () => void;
  pause: () => void;
  togglePlayPause: () => void;
  seek: (time: number) => void;
  setPlaybackSpeed: (speed: number) => void;
  
  // UI controls
  toggleFullscreen: () => void;
  setShowControls: (show: boolean) => void;
  
  // Canvas controls
  setCanvasSize: (width: number, height: number, scale: number) => void;
  
  // Time updates
  updateCurrentTime: (time: number) => void;
  setHasAudioError: (hasError: boolean) => void;
  
  // Reset
  reset: () => void;
}

export const usePlayerStore = create<PlayerState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    recording: null,
    events: [],
    duration: 0,
    
    isPlaying: false,
    currentTime: 0,
    playbackSpeed: 1,
    
    audioUrl: null,
    audioElement: null,
    isAudioReady: false,
    hasAudioError: false,
    
    canvasWidth: 0,
    canvasHeight: 0,
    scale: 1,
    
    isFullscreen: false,
    isSeeking: false,
    showControls: true,
    
    // Actions
    setRecording: (recording) => {
      const events = recording.events || [];
      let duration = recording.duration || 0;
      
      // If duration is 0, calculate from last event timestamp
      if (duration === 0 && events.length > 0) {
        const lastEvent = events[events.length - 1];
        duration = lastEvent.timestamp || 0;
      }
      
      set({ 
        recording, 
        events,
        duration,
        currentTime: 0,
        isPlaying: false 
      });
    },
    
    setAudioUrl: (url) => set({ audioUrl: url }),
    
    setAudioElement: (element) => {
      // Set up audio element event listeners
      if (element) {
        element.oncanplaythrough = () => {
          set({ isAudioReady: true });
        };
        element.onerror = () => {
          set({ isAudioReady: false, hasAudioError: true });
        };
        element.onloadedmetadata = () => {
          // Update duration if not set or different
          const { duration } = get();
          if (!duration || Math.abs(duration - element.duration * 1000) > 1000) {
            set({ duration: element.duration * 1000 });
          }
        };
      }
      set({ audioElement: element });
    },
    
    // Playback controls
    play: () => {
      const { currentTime, duration, isPlaying } = get();
      
      // Prevent rapid play calls
      if (isPlaying) {
        return;
      }
      
      // If at the end, reset to beginning
      if (currentTime >= duration && duration > 0) {
        set({ currentTime: 0 });
      }
      
      // Just set the state - AudioSync will handle the actual play
      set({ isPlaying: true });
    },
    
    pause: () => {
      const { audioElement, isPlaying } = get();
      
      // Prevent rapid pause calls
      if (!isPlaying) {
        return;
      }
      
      if (audioElement) {
        audioElement.pause();
      }
      set({ isPlaying: false });
    },
    
    togglePlayPause: () => {
      const { isPlaying } = get();
      
      // Add a small delay to prevent double-clicks
      const now = Date.now();
      const lastToggle = (get() as any).lastToggleTime || 0;
      if (now - lastToggle < 100) {
        return;
      }
      (set as any)({ lastToggleTime: now });
      
      if (isPlaying) {
        get().pause();
      } else {
        get().play();
      }
    },
    
    seek: (time) => {
      const { audioElement, duration } = get();
      const clampedTime = Math.max(0, Math.min(time, duration));
      
      set({ currentTime: clampedTime, isSeeking: true });
      
      if (audioElement) {
        audioElement.currentTime = clampedTime / 1000; // Convert to seconds
      }
      
      // Reset seeking flag after a short delay
      setTimeout(() => set({ isSeeking: false }), 100);
    },
    
    setPlaybackSpeed: (speed) => {
      const { audioElement } = get();
      if (audioElement) {
        audioElement.playbackRate = speed;
      }
      set({ playbackSpeed: speed });
    },
    
    // UI controls
    toggleFullscreen: () => {
      set((state) => ({ isFullscreen: !state.isFullscreen }));
    },
    
    setShowControls: (show) => set({ showControls: show }),
    
    // Canvas controls
    setCanvasSize: (width, height, scale) => {
      set({ canvasWidth: width, canvasHeight: height, scale });
    },
    
    // Time updates
    updateCurrentTime: (time) => {
      set({ currentTime: time });
    },
    
    setHasAudioError: (hasError) => {
      set({ hasAudioError: hasError });
    },
    
    // Reset
    reset: () => {
      const { audioElement } = get();
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
      
      set({
        recording: null,
        events: [],
        duration: 0,
        isPlaying: false,
        currentTime: 0,
        playbackSpeed: 1,
        audioUrl: null,
        audioElement: null,
        isAudioReady: false,
        hasAudioError: false,
        canvasWidth: 0,
        canvasHeight: 0,
        scale: 1,
        isFullscreen: false,
        isSeeking: false,
        showControls: true,
      });
    },
  }))
);

// Selector hooks for specific state slices
export const usePlaybackState = () => usePlayerStore((state) => ({
  isPlaying: state.isPlaying,
  currentTime: state.currentTime,
  duration: state.duration,
  playbackSpeed: state.playbackSpeed,
}));

export const usePlayerControls = () => usePlayerStore((state) => ({
  play: state.play,
  pause: state.pause,
  togglePlayPause: state.togglePlayPause,
  seek: state.seek,
  setPlaybackSpeed: state.setPlaybackSpeed,
}));

export const useCanvasState = () => usePlayerStore((state) => ({
  canvasWidth: state.canvasWidth,
  canvasHeight: state.canvasHeight,
  scale: state.scale,
  setCanvasSize: state.setCanvasSize,
}));